import { Box, Container, Typography, Divider, Paper } from "@mui/material";
import { useState, useMemo } from "react";

import { useGetPublicTdsQuery } from "api/public/technicalDecisions/technicalDecisions.api";
import {
  PublicTechnicalDecisionTableItem,
  TD_STATUS,
  TdStatus,
} from "api/public/technicalDecisions/technicalDecisions.types";
import Subheader from "components/Subheader";
import { usePageTitle } from "hooks/usePageTitle";

import TdCountToggles from "./components/TdCountToggles";
import TechDecisionsTable from "./components/TdTable/TechDecisionsTable";

const ARCHIVED_STATUSES: TdStatus[] = [TD_STATUS.ARCHIVED];
const ACTIVE_STATUSES: TdStatus[] = [TD_STATUS.VALID, TD_STATUS.PUBLISHED];
const EMPTY_TDS: PublicTechnicalDecisionTableItem[] = [];

export default function TechnicalDecisions() {
  usePageTitle("NIAP - Technical Decisions");
  const [showArchived, setShowArchived] = useState(false);

  const { data: tds = EMPTY_TDS, isLoading } = useGetPublicTdsQuery();

  const { activeTds, archivedTds } = useMemo(
    () => ({
      archivedTds: tds.filter((td) => ARCHIVED_STATUSES.includes(td.status)),
      activeTds: tds.filter((td) => ACTIVE_STATUSES.includes(td.status)),
    }),
    [tds],
  );

  const activeCount = activeTds.length;
  const archiveCount = archivedTds.length;

  return (
    <>
      <Subheader pageName={"Technical Decisions"} />

      <Container
        maxWidth="lg"
        sx={{
          mt: 3,
        }}
      >
        <Typography variant="h3" fontWeight="bold" my={2}>
          PROTECTION PROFILES - TECHNICAL DECISIONS
        </Typography>

        <Typography variant="body1" my={2}>
          Technical Decisions (TDs) are issued to correct errors or clarify
          Security Functional Requirements (SFRs) and Assurance Activities
          within NIAP-approved Protection Profiles (PPs). Technical Decisions
          are effective upon publication and must be incorporated into all
          current and future evaluations. Current evaluations include all
          evaluations except those for which a complete Check-Out package has
          been submitted to NIAP for final validation team review.
        </Typography>

        <Typography variant="body1" my={2}>
          In the rare case that a TD is issued which includes new SFRs or AAs,
          NIAP will make the determination for when products in evaluation must
          comply and include an effective date in the TD.
        </Typography>

        <Divider sx={{ borderColor: "black", my: 1 }} />

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          px={5}
        >
          <Typography variant="h3">
            {showArchived ? "ARCHIVED" : "ACTIVE"}
            <br />
            {`TECHNICAL DECISIONS - ${showArchived ? archiveCount : activeCount}`}
          </Typography>

          <Box display="flex" py={2} justifyContent="flex-end" flex={1}>
            <TdCountToggles
              activeCount={activeCount}
              archiveCount={archiveCount}
              onClick={setShowArchived}
            />
          </Box>
        </Box>

        <Paper elevation={2} sx={{ mb: 4 }}>
          <TechDecisionsTable
            archived={showArchived}
            activeTds={activeTds}
            archivedTds={archivedTds}
            loading={isLoading}
          />
        </Paper>
      </Container>
    </>
  );
}
